import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { eur } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import AgendaCalendar from "@/components/AgendaCalendar";
import { Target, ShieldCheck, Handshake, AlertTriangle, Loader2, TrendingUp, TrendingDown, Minus, Gauge, Radar, CalendarClock, ChevronLeft, ChevronRight, ExternalLink, FileWarning } from "lucide-react";

const VAL_STYLE = {
  POSITIVA: "bg-emerald-500 text-emerald-950",
  ADEGUATA: "bg-primary text-primary-foreground",
  "DA MIGLIORARE": "bg-amber-500 text-amber-950",
  INSUFFICIENTE: "bg-red-500 text-red-950",
};
const TREND_ICON = { MIGLIORAMENTO: TrendingUp, PEGGIORAMENTO: TrendingDown, STABILE: Minus };

const LEVEL_CHIP = {
  ok: "bg-emerald-500/15 text-emerald-400",
  warn: "bg-amber-500/15 text-amber-400",
  bad: "bg-red-500/15 text-red-400",
};
const LEVEL_LABEL = { ok: "in linea", warn: "quasi in linea", bad: "sotto target" };

const Chip = ({ ok, level }) => {
  const lv = level !== undefined ? level : (ok === null || ok === undefined ? null : ok ? "ok" : "bad");
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${lv ? LEVEL_CHIP[lv] : "bg-muted text-muted-foreground"}`}>
      {lv ? LEVEL_LABEL[lv] : "N/D"}
    </span>
  );
};

const MetricPill = ({ label, value, n, pct, active, onClick }) => (
  <button type="button" onClick={onClick} className={`rounded-lg border p-2.5 text-center transition-colors ${active ? "border-primary bg-primary/20 ring-1 ring-primary" : "border-primary/25 bg-primary/[0.06]"} ${onClick ? "cursor-pointer hover:border-primary/60" : "cursor-default"}`} data-testid={`az-metric-${label}`}>
    <div className="text-[10px] font-bold uppercase leading-tight tracking-wide text-muted-foreground">{label}</div>
    <div className="mt-1 font-heading text-lg font-black text-primary">{value}/{n}{pct != null ? <span className="ml-1 text-sm font-bold text-foreground">{pct}%</span> : null}</div>
  </button>
);

const Tile = ({ icon: Icon, label, value, ok, level, hint, testid, onClick, big, className = "" }) => (
  <div onClick={onClick} className={`flex flex-col rounded-lg border ${big ? "border-primary/40 bg-primary/[0.07] p-4" : "border-white/10 bg-white/[0.03] p-3"} ${onClick ? "cursor-pointer transition-colors hover:border-primary/40 hover:bg-white/[0.07]" : ""} ${className}`} data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className={`flex items-center gap-1.5 font-bold uppercase tracking-wide text-muted-foreground ${big ? "text-xs" : "text-[10px]"}`}><Icon className={big ? "h-4 w-4" : "h-3.5 w-3.5"} /> {label}</span>
      {(ok !== undefined || level !== undefined) && <Chip ok={ok} level={level} />}
    </div>
    <div className={`mt-1 font-heading font-black leading-none ${big ? "text-5xl md:text-6xl" : "text-2xl"}`}>{value}</div>
    {hint && <div className={`mt-1 leading-snug text-muted-foreground ${big ? "text-[11px]" : "text-[10px] mt-0.5"}`}>{hint}</div>}
  </div>
);

export default function AgencyScorecard({ agencyId } = {}) {
  const [d, setD] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [week, setWeek] = useState(0);
  const navigate = useNavigate();
  const [calOpen, setCalOpen] = useState(false);
  const [calItems, setCalItems] = useState(null);
  const [azOpen, setAzOpen] = useState(false);
  const [compOpen, setCompOpen] = useState(false);
  const [azTipo, setAzTipo] = useState("clienti");
  const [azData, setAzData] = useState(null);
  const [azFilter, setAzFilter] = useState(null);
  const q = agencyId ? `&agency_id=${agencyId}` : "";

  const openAzioni = (tipo) => {
    setAzTipo(tipo); setAzData(null); setAzFilter(null); setAzOpen(true);
    api.get(`/dashboard/my-valutazione/azioni?tipo=${tipo}&week=${week}${q}`).then((r) => setAzData(r.data)).catch(() => setAzData({ anagrafiche: [], totale: 0 }));
  };
  const openScheda = (id) => {
    setAzOpen(false);
    navigate(azTipo === "prospect" ? `/prospect/${id}` : `/partners/${id}`);
  };

  useEffect(() => {
    if (calOpen && calItems === null) {
      api.get(`/dashboard/my-valutazione/pianificate?week=${week}${q}`).then((r) => setCalItems(r.data.items || [])).catch(() => setCalItems([]));
    }
  }, [calOpen, calItems, week, q]);

  useEffect(() => {
    setD(null); setAi(null); setAiLoading(true); setErr(false); setCalItems(null);
    api.get(`/dashboard/my-valutazione?week=${week}${q}`).then((r) => setD(r.data)).catch(() => setErr(true));
    api.get(`/dashboard/my-valutazione/giudizio?week=${week}${q}`).then((r) => setAi(r.data)).catch(() => {}).finally(() => setAiLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [week, agencyId]);

  if (err) return null;

  const azMatch = (g) => {
    if (!azFilter) return true;
    if (["A", "B", "C"].includes(azFilter)) return (g.categoria || "").toUpperCase() === azFilter;
    return (g.azioni || []).some((a) => a[azFilter]);
  };
  const azVisible = (azData?.anagrafiche || []).filter(azMatch);
  if (!d) return (
    <Card className="mb-6 flex items-center gap-3 border-primary/30 bg-primary/5 p-5" data-testid="agency-scorecard-loading">
      <Loader2 className="h-4 w-4 animate-spin text-primary" /> <span className="text-sm text-muted-foreground">Carico la tua valutazione…</span>
    </Card>
  );

  const cur = d.corrente || {};
  const pctVen = d.pct_impegno;
  const pctPres = d.pct_presidio_ab;
  const conv = (d.ultimi_30g || {}).conversioni || 0;
  const rischio = d.clienti_a_rischio || 0;
  const p2w = d.pianificate_2w_pct;
  const p2wLevel = p2w == null ? null : (p2w > 70 ? "ok" : (p2w > 50 ? "warn" : "bad"));
  const ac = d.azioni_clienti_pct;
  const acLevel = ac == null ? null : (ac >= 100 ? "ok" : (ac >= 70 ? "warn" : "bad"));
  const comp = d.compliance_pct;
  const compLevel = comp == null ? null : (comp >= 90 ? "ok" : (comp >= 70 ? "warn" : "bad"));
  const TrendIcon = TREND_ICON[(ai?.trend || "STABILE").toUpperCase()] || Minus;

  return (
    <Card className="mb-6 border-primary/40 bg-primary/[0.06] p-5" data-testid="agency-scorecard">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Gauge className="h-5 w-5 text-primary" />
          <div>
            <h2 className="font-heading text-lg font-black leading-tight">La tua valutazione{week < 0 ? " · settimana precedente" : ""}</h2>
            <p className="text-[11px] text-muted-foreground">Giudizio della Direzione · {d.periodo || "ultimi 14 giorni"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-testid="agency-scorecard-verdict">
          {aiLoading ? (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> giudizio in preparazione…</span>
          ) : ai ? (
            <>
              <span className={`rounded-full px-3 py-1 text-xs font-black tracking-wide ${VAL_STYLE[(ai.valutazione || "").toUpperCase()] || "bg-muted text-foreground"}`}>{ai.valutazione || "—"}</span>
              <span className="flex items-center gap-1 text-xs font-semibold text-muted-foreground"><TrendIcon className="h-3.5 w-3.5" /> {ai.trend || "STABILE"}</span>
            </>
          ) : null}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setWeek((w) => w - 1)} data-testid="scorecard-prev-week">
          <ChevronLeft className="mr-1 h-4 w-4" /> Valutazione della settimana precedente
        </Button>
        {week < 0 && (
          <Button variant="ghost" size="sm" onClick={() => setWeek((w) => Math.min(0, w + 1))} data-testid="scorecard-next-week">
            Settimana successiva <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        )}
        {week < 0 && (
          <Button variant="ghost" size="sm" onClick={() => setWeek(0)} data-testid="scorecard-current-week">
            Torna alla settimana corrente
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-8">
        <Tile testid="sc-compliance" big className="col-span-2 md:row-span-2 lg:row-span-2" icon={Gauge} label="Compliance" value={comp != null ? `${comp}%` : "—"} level={compLevel} onClick={() => setCompOpen(true)} hint={`presidio ${d.compliance_presidio_pct ?? "–"}% · prospecting ${d.compliance_prospecting_pct ?? "–"}% · opportunità ${d.compliance_opportunita_pct ?? "–"}% · pianificazione ${d.compliance_pianificazione_pct ?? "–"}% · disciplina ${d.compliance_disciplina_pct ?? "–"}% · tocca per il dettaglio`} />
        <Tile testid="sc-vendite" icon={Target} label="Obiettivo vendite" value={pctVen != null ? `${pctVen}%` : "—"} ok={pctVen == null ? null : pctVen >= 100} hint={`${eur(cur.fatturato || 0)} / ${eur(d.impegno_vendita || 0)} (settimana)`} />
        <Tile testid="sc-presidio" icon={ShieldCheck} label="Azioni su clienti" value={`${d.azioni_clienti_svolte ?? 0}/${d.azioni_clienti_target ?? 0}`} level={acLevel} hint={`azioni su clienti A/B/C · target di presidio${d.azioni_clienti_pct != null ? ` · ${d.azioni_clienti_pct}%` : ""} · tocca per dettaglio`} onClick={() => openAzioni("clienti")} />
        <Tile testid="sc-conversioni" icon={Handshake} label="Partner convertiti" value={conv} ok={conv >= 1} hint="ultimi 30 giorni" />
        <Tile testid="sc-prospecting" icon={Radar} label="Prospect lavorati" value={d.prospecting_previste ? `${d.prospecting_svolte}/${d.prospecting_previste}` : (d.prospecting_svolte ?? 0)} ok={d.prospecting_previste ? d.prospecting_svolte >= d.prospecting_previste : null} hint={`prospect raggiunti su ${d.prospecting_azioni ?? 0} azioni${d.prospecting_senza_note ? ` · ${d.prospecting_senza_note} senza note (compilale!)` : ""} · obiettivo ${d.prospecting_settimana || 0}/sett${d.prospecting_pct != null ? ` · ${d.prospecting_pct}%` : ""} · tocca per dettaglio`} onClick={() => openAzioni("prospect")} />
        <Tile testid="sc-pianificate-2w" icon={CalendarClock} label="Pianificate 2 sett." value={d.pianificate_2w ?? 0} level={p2wLevel} hint={`prossime 2 settimane · obiettivo ${d.pianificate_2w_target ?? 24}${d.pianificate_2w_pct != null ? ` · ${d.pianificate_2w_pct}%` : ""} · tocca per vedere`} onClick={() => setCalOpen(true)} />
        <Tile testid="sc-rischio" icon={AlertTriangle} label="Clienti a rischio" value={rischio} ok={rischio === 0} hint="alto valore in calo/inattivi · tocca per dettaglio" onClick={() => openAzioni("rischio")} />
        <Tile testid="sc-contratti-scaduti" icon={FileWarning} label="Contratto scaduto" value={d.contratti_scaduti ?? 0} ok={(d.contratti_scaduti ?? 0) === 0} hint={`${d.contratti_in_scadenza ?? 0} in scadenza (≤60gg) · ${d.contratti_senza ?? 0} senza contratto`} />
      </div>

      {!aiLoading && ai?.valutazione_direzione && (
        <div className="mt-4 rounded-lg border border-white/10 bg-black/20 p-3" data-testid="agency-scorecard-note">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-primary">Nota della Direzione</div>
          <p className="whitespace-pre-line text-xs leading-relaxed text-foreground/90">{ai.valutazione_direzione}</p>
          {Array.isArray(ai.da_migliorare) && ai.da_migliorare.length > 0 && (
            <ul className="mt-2 space-y-1">
              {ai.da_migliorare.map((x, i) => (
                <li key={i} className="text-[11px] text-muted-foreground"><b className="text-foreground">{x.kpi}:</b> {x.risultato}{x.scostamento ? ` · ${x.scostamento}` : ""}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      <Dialog open={compOpen} onOpenChange={setCompOpen}>
        <DialogContent className="max-w-2xl" data-testid="scorecard-compliance-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Gauge className="h-5 w-5 text-primary" /> Compliance {comp != null ? `· ${comp}%` : ""}</DialogTitle>
            <DialogDescription>Come si calcola e il calcolo effettivo del periodo.</DialogDescription>
          </DialogHeader>
          {(() => {
            const det = d.compliance_detail;
            if (!det) return <div className="p-4 text-sm text-muted-foreground">Dettaglio non disponibile.</div>;
            return (
              <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1" data-testid="compliance-detail-body">
                <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                  <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-primary">1 · Come si calcola</div>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    La Compliance è la <b className="text-foreground">media pesata</b> di 5 pilastri ({det.pilastri.map((p) => `${p.label} ${p.peso}`).join(" · ")}). Calcolo <b className="text-foreground">cumulativo</b> dal <b className="text-foreground">{det.periodo_da}</b> a oggi, contando <b className="text-foreground">solo i giorni feriali</b>{det.giorni_feriali != null ? ` (${det.giorni_feriali} finora)` : ""} — sabato, domenica e festivi esclusi. Ogni pilastro dà <b className="text-foreground">punti = score% × peso ÷ 100</b>; i pilastri senza dati vengono esclusi e i pesi rinormalizzati (voto = punti totali ÷ pesi disponibili).
                  </p>
                  <ul className="mt-2 space-y-1.5">
                    {det.pilastri.map((p) => (
                      <li key={p.key} className="text-xs">
                        <span className="font-semibold text-foreground">{p.label} <span className="text-primary">({p.peso}%)</span></span>{" — "}
                        <span className="text-muted-foreground">{p.teoria}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-lg border border-white/10 bg-black/20 p-3">
                  <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-primary">2 · Come nasce il tuo {det.risultato != null ? `${det.risultato}%` : "voto"} · dal {det.periodo_da} a oggi</div>
                  <div className="space-y-2">
                    {det.pilastri.map((p) => {
                      const na = p.score == null;
                      return (
                        <div key={p.key} className="rounded border border-white/5 bg-white/[0.02] p-2" data-testid={`compliance-pillar-${p.key}`}>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-semibold text-foreground">{p.label} <span className="text-muted-foreground">· vale {p.peso}%</span></span>
                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${na ? "bg-muted text-muted-foreground" : "bg-primary/15 text-primary"}`}>{na ? "Non valutata" : `${p.score}%`}</span>
                          </div>
                          <div className="mt-0.5 text-[11px] text-muted-foreground">{p.calcolo}</div>
                          {na
                            ? <div className="mt-0.5 text-[10px] text-amber-500">Non ancora valutata: non pesa sul voto</div>
                            : <div className="mt-0.5 text-[10px] text-muted-foreground">Porta <b className="text-foreground">{String(p.punti).replace(".", ",")}</b> punti su {p.peso}</div>}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 border-t border-white/10 pt-2 text-xs" data-testid="compliance-formula-total">
                    <div className="text-muted-foreground">Totale: <b className="text-foreground">{String(det.punti_totali).replace(".", ",")} punti</b> su {det.pesi_disponibili} disponibili = <b className="text-primary">{det.risultato != null ? `${det.risultato}%` : "—"}</b></div>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
      <Dialog open={calOpen} onOpenChange={setCalOpen}>
        <DialogContent className="max-w-5xl" data-testid="scorecard-cal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CalendarClock className="h-5 w-5 text-primary" /> Appuntamenti · prossime 2 settimane</DialogTitle>
            <DialogDescription>Sola visualizzazione — per modificare vai in Agenda.</DialogDescription>
          </DialogHeader>
          {calItems === null ? (
            <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carico gli appuntamenti…</div>
          ) : (
            <div className="max-h-[72vh] overflow-y-auto pr-1" data-testid="scorecard-cal-body"><AgendaCalendar items={calItems} mode="2weeks" readOnly kmFilters={agencyId ? { agency_id: agencyId } : null} /></div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={azOpen} onOpenChange={setAzOpen}>
        <DialogContent className="max-w-2xl" data-testid="scorecard-azioni-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">{azTipo === "prospect" ? <><Radar className="h-5 w-5 text-primary" /> Prospect lavorati · settimana</> : azTipo === "rischio" ? <><AlertTriangle className="h-5 w-5 text-primary" /> Clienti a rischio</> : <><ShieldCheck className="h-5 w-5 text-primary" /> Azioni su clienti A/B/C · settimana</>}</DialogTitle>
            <DialogDescription>{azTipo === "rischio" ? "Clienti classificati a rischio — espandi per vedere le ultime azioni." : "Raggruppate per anagrafica — espandi per vedere le singole azioni."}</DialogDescription>
          </DialogHeader>
          {azData === null ? (
            <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carico le azioni…</div>
          ) : (azData.anagrafiche || []).length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground" data-testid="scorecard-azioni-empty">{azTipo === "rischio" ? "Nessun cliente a rischio." : "Nessuna azione nel periodo."}</div>
          ) : (
            <div className="max-h-[65vh] overflow-y-auto pr-1" data-testid="scorecard-azioni-body">
              <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                <span>{azTipo === "rischio" ? `${azVisible.length} clienti a rischio` : `${azVisible.length}${azFilter ? ` / ${azData.anagrafiche.length}` : ""} anagrafiche · ${azData.totale} azioni totali`}</span>
                {azFilter && <button type="button" onClick={() => setAzFilter(null)} className="font-semibold text-primary hover:underline" data-testid="az-filter-clear">Mostra tutti</button>}
              </div>
              {azTipo === "clienti" && (() => {
                const c = { A: 0, B: 0, C: 0 };
                (azData.anagrafiche || []).forEach((g) => { const k = (g.categoria || "").toUpperCase(); if (c[k] != null) c[k] += 1; });
                return (
                  <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="az-cat-breakdown">
                    <span className="text-[11px] font-semibold text-muted-foreground">Filtra per categoria:</span>
                    {["A", "B", "C"].map((k) => (
                      <button key={k} type="button" onClick={() => setAzFilter((f) => (f === k ? null : k))} className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold transition-colors ${azFilter === k ? "bg-primary text-primary-foreground ring-1 ring-primary" : "bg-primary/15 text-primary hover:bg-primary/25"}`} data-testid={`az-cat-${k}`}>{k}: {c[k]}</button>
                    ))}
                  </div>
                );
              })()}
              {(azTipo === "prospect" || azTipo === "clienti") && azData.metriche && (
                <div className="mb-3" data-testid="az-metriche">
                  <div className="grid grid-cols-3 gap-2">
                    <MetricPill label="Raggiungimento" value={azData.metriche.raggiunti} n={azData.metriche.n_prospect} pct={azData.metriche.raggiunti_pct} active={azFilter === "raggiunto"} onClick={() => setAzFilter((f) => (f === "raggiunto" ? null : "raggiunto"))} />
                    <MetricPill label="Accesso Decision Maker" value={azData.metriche.accesso_dm} n={azData.metriche.n_prospect} pct={azData.metriche.accesso_dm_pct} active={azFilter === "accesso_dm"} onClick={() => setAzFilter((f) => (f === "accesso_dm" ? null : "accesso_dm"))} />
                    <MetricPill label="Generazione visita" value={azData.metriche.visite_generate} n={azData.metriche.n_prospect} pct={azData.metriche.visite_generate_pct} active={azFilter === "visita_generata"} onClick={() => setAzFilter((f) => (f === "visita_generata" ? null : "visita_generata"))} />
                  </div>
                  {azData.metriche.n_classificati < azData.metriche.n_prospect && (
                    <div className="mt-1.5 text-[10px] text-amber-500" data-testid="az-metriche-warning">⚠ {azData.metriche.n_prospect - azData.metriche.n_classificati} anagrafiche con azioni non ancora classificate dall'AI: i numeri si completano man mano che le note vengono lette.</div>
                  )}
                </div>
              )}
              {azVisible.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground" data-testid="az-filter-empty">Nessuna anagrafica per il filtro selezionato.</div>
              ) : (
              <Accordion type="multiple" className="space-y-1.5">
                {azVisible.map((g) => (
                  <AccordionItem key={g.id} value={g.id} className="rounded-lg border border-white/10 bg-white/[0.03] px-3">
                    <AccordionTrigger className="py-2 text-sm hover:no-underline" data-testid={`az-group-${g.id}`}>
                      <span className="flex w-full items-center justify-between gap-2 pr-2">
                        <span className="flex min-w-0 flex-col text-left">
                          <span className="truncate font-semibold">{g.nome}{g.categoria ? <span className="ml-1.5 rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-bold text-primary">{g.categoria}</span> : null}{g.altra_agenzia ? <span className="ml-1.5 rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-bold text-amber-500" data-testid={`az-altra-agenzia-${g.id}`} title={g.assegnataria ? `Anagrafica assegnata a: ${g.assegnataria}` : "Anagrafica non assegnata a questo agente"}>⚠ {g.assegnataria ? `di ${g.assegnataria}` : "non assegnato a lui"}</span> : null}</span>
                          {g.sottotitolo ? <span className="truncate text-[10px] font-normal text-muted-foreground">{g.sottotitolo}</span> : null}
                        </span>
                        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold">{g.count} azioni</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <button onClick={() => openScheda(g.id)} className="mb-2 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid={`az-open-${g.id}`}><ExternalLink className="h-3 w-3" /> Apri scheda {azTipo === "prospect" ? "prospect" : "cliente"}</button>
                      {g.azioni.length === 0 ? (
                        <div className="pb-2 text-xs text-muted-foreground">Nessuna azione registrata.</div>
                      ) : (
                        <ul className="space-y-1 pb-2">
                          {g.azioni.map((a, i) => (
                            <li key={i} className="rounded border border-white/5 bg-black/20 px-2 py-1 text-xs" data-testid={`az-action-${g.id}-${i}`}>
                              <span className="font-mono text-muted-foreground">{a.date}{a.time ? ` ${a.time}` : ""}</span> · <span className="font-semibold">{a.tipo}</span>{a.esito ? ` · ${a.esito}` : ""}
                              {a.note ? <div className="mt-0.5 text-muted-foreground">{a.note}</div> : null}
                            </li>
                          ))}
                        </ul>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
